import { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { directionOf } from "@/engine/support/locales";
import { getLocale, subscribe, syncLocaleCookie } from "@/engine/state/locale";
import { applyLocale, createI18n } from "@/providers/i18n";

/** Puts the resolved language and its direction on `<html>`. */
function declareDocumentLanguage(locale: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = directionOf(locale);
}

/**
 * The catalogue in the locale this render is in.
 *
 * On the server that is a fresh instance, because the request next to this one
 * may be in another language. On the client it is the singleton, moved to the
 * locale the server resolved — during the render rather than from an effect,
 * since an effect runs after React has already compared this pass against the
 * server's HTML, which is the mismatch #310 is about.
 */
function useI18nInstance(locale: string) {
  // Once, at mount: after that the store decides, and re-applying the locale
  // the page was loaded in would undo every language the visitor picks.
  const [instance] = useState(() =>
    import.meta.env.SSR ? createI18n(locale) : applyLocale(locale),
  );
  return instance;
}

/**
 * Carries the catalogue and follows the locale store.
 *
 * `locale` is what the render was resolved to — the cookie or `Accept-Language`
 * on the server, and the same value handed to the client as loader data. From
 * the first change after that the store decides, which is what the effect
 * follows: it moves the singleton, corrects `<html>`, and mirrors the choice
 * into the cookie so the next request is served in it.
 */
export function I18nProvider({
  locale,
  children,
}: {
  locale: string;
  children: React.ReactNode;
}) {
  const instance = useI18nInstance(locale);

  useEffect(() => {
    const apply = () => {
      const current = getLocale();
      applyLocale(current);
      declareDocumentLanguage(current);
      syncLocaleCookie();
    };
    apply();
    return subscribe(apply);
  }, []);

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}
